import { t } from '@hefe/i18n';
import {
  PracticeTimesScheduleEntry,
  RequestBasePostPracticeTimesScheduleEntry,
} from '@hefe/models';
import {
  BasketballPracticeTimesApi,
  BasketballTeamsSlice,
  GymsSlice,
  ModalsSlice,
} from '@hefe/store';
import { formatTime } from '@hefe/utils';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';

interface AddEditPracticeTimesScheduleEntryFormProps {
  teamId: number;
  entry?: PracticeTimesScheduleEntry;
  scheduleId: number;
  weekday?: number;
}

export default function AddEditPracticeTimesScheduleEntryForm(
  props: AddEditPracticeTimesScheduleEntryFormProps
) {
  const { teamId, entry } = props;

  const [saveTime, { isLoading }] =
    BasketballPracticeTimesApi.useCreatePracticeTimesScheduleEntryMutation();

  const dispatch = useAppDispatch();
  const gyms = useSelector(GymsSlice.gymsSelectSelector);
  const teamsSelect = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const [refresh, doRefresh] = useState(false);

  useEffect(() => {
    doRefresh((prev) => !prev);
  }, [teamId, entry?.id]);

  const onSubmit = (values: any, resetForm: any) => {
    const closeModal = ModalsSlice.modalsActions.closeModal;
    const setModalLoading = ModalsSlice.modalsActions.setModalLoading;

    dispatch(setModalLoading(true));
    const data: RequestBasePostPracticeTimesScheduleEntry = {
      id: entry?.id ? entry.id : undefined,
      basketball_team_ids: values.basketball_team_ids,
      locationable_type: 'App\\Models\\Base\\Gym',
      locationable_id: values.gym_id,
      weekday: values.weekday,
      time: formatTime(values.time),
      duration: values.duration,
      split_location: values.split_location,
      basketball_response: true,
    };

    if (!data.id) {
      delete data.id;
    }

    saveTime({
      scheduleId: props.scheduleId,
      request: data,
    })
      .unwrap()
      .then(() => {
        resetForm();
        dispatch(closeModal());
      })
      .catch(() => null)
      .finally(() => dispatch(setModalLoading(false)));
  };

  const weekdays = () => {
    const days = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];

    return days.map((day: string, index: number) => {
      return {
        value: index,
        label: t('weekdays.' + day),
      };
    });
  };

  return (
    <CustomForm
      refresh={refresh}
      key={`add-edit-team-practice-times-schedule-entry-form-${
        entry ? entry.id : teamId ? teamId : 'new'
      }-${props.weekday}`}
      formId={`add-edit-team-practice-times-schedule-entry-form-${
        entry ? entry.id : teamId ? teamId : 'new'
      }`}
      fields={[
        {
          name: 'basketball_team_ids',
          label: t('teams'),
          type: 'select',
          selectOptions: teamsSelect,
          multiSelect: true,
          initalValue: entry?.basketball_teams?.map((team) => team.id) ?? [
            teamId,
          ],
          rules: [
            {
              required: true,
              message: t('form_errors.value'),
            },
          ],
        },
        {
          name: 'weekday',
          label: t('weekday'),
          type: 'select',
          selectOptions: weekdays(),
          initalValue: props.weekday ?? entry?.weekday,
          rules: [
            {
              required: true,
              message: t('form_errors.weekday'),
            },
          ],
        },
        {
          name: 'gym_id',
          label: t('gym'),
          type: 'select',
          initalValue: entry?.gym?.id,
          selectOptions: gyms,
          rules: [
            {
              required: true,
              message: t('form_errors.gym'),
            },
          ],
        },
        {
          name: 'time',
          label: t('time'),
          type: 'time',
          initalValue: entry?.time,
          rules: [
            {
              required: true,
              message: t('form_errors.time'),
            },
          ],
        },
        {
          name: 'duration',
          label: `${t('duration')} (${t('in_min')})`,
          type: 'number',
          initalValue: entry?.duration,
          rules: [
            {
              required: true,
              message: t('form_errors.number'),
            },
          ],
        },
        {
          name: 'split_location',
          label: t('practice_time.split_gym.title'),
          type: 'checkbox',
          initalValue: entry?.split_location,
          extraLabel: t('practice_time.split_gym.description'),
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
